/**
 * Web Audio node graphs. Everything here talks to an `AudioContext`; nothing
 * here decides anything — the decisions are in `policy.ts`.
 *
 * Everything is synthesised, out of oscillators and filtered noise. No
 * recording is fetched, so the game is audible on the first frame of the
 * first battle and a lost asset cannot silence it. The beds — sea, wind and
 * the engine — are continuous and parametric, which a recording cannot be
 * without a bank of loops per state.
 *
 * Every continuous parameter moves with `setTargetAtTime`, which eases on the
 * *audio* clock at a fixed time constant. Feed the engine the same throttle
 * at 30 fps and at 144 fps and it arrives at the same place at the same
 * wall-clock moment; the frame rate cannot get into the result.
 */
import type { VoiceSpec } from './policy';
import type { SampleSet } from './samples';

/**
 * `exponentialRampToValueAtTime` refuses to reach zero, so decays land here
 * and the node is then stopped. Far below hearing.
 */
const SILENCE = 0.0001;
/** Seconds a bed parameter takes to travel most of the way to a new value. */
const BED_GLIDE = 0.25;
const ENGINE_GLIDE = 0.12;

/**
 * Two seconds of white noise, generated once and shared by every voice. A
 * fresh buffer per shot would allocate 96 000 floats for a 70 ms crack, and
 * in a firefight that is garbage collection during combat. Voices vary it by
 * starting at a random offset and detuning the playback rate instead.
 */
let noiseBuffer: AudioBuffer | null = null;

function sharedNoise(ctx: BaseAudioContext): AudioBuffer {
  if (noiseBuffer && noiseBuffer.sampleRate === ctx.sampleRate) return noiseBuffer;
  const seconds = 2;
  const buffer = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  noiseBuffer = buffer;
  return buffer;
}

export function noiseSource(ctx: BaseAudioContext, loop: boolean): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = sharedNoise(ctx);
  src.loop = loop;
  return src;
}

/**
 * Cutoff of the distance lowpass, Hz: full band close by, down to a rumble
 * at the audible limit. Exponential in distance, so the fall is even to the
 * ear.
 */
export function distanceCutoff(distance: number): number {
  if (distance <= 150) return 18000;
  const k = Math.min(1, (distance - 150) / 8000);
  return 18000 * Math.pow(500 / 18000, k);
}

/** How much of a positioned voice goes to the reverb, 0…1, by distance. */
export function reverbSend(distance: number): number {
  return Math.min(0.7, distance / 4000);
}

/**
 * A shared reverb: a convolver on a generated impulse (exponentially
 * decaying noise, two seconds, brighter at the start), fed by every
 * positioned voice in proportion to its distance. One node for the whole
 * mix, built once.
 */
export function buildReverb(ctx: AudioContext, destination: AudioNode): { input: GainNode } {
  const seconds = 2.2;
  const rate = ctx.sampleRate;
  const impulse = ctx.createBuffer(2, Math.floor(rate * seconds), rate);
  for (let c = 0; c < 2; c++) {
    const d = impulse.getChannelData(c);
    for (let i = 0; i < d.length; i++) {
      const t = i / rate;
      // Early reflections dense and bright, tail long and dull.
      const env = Math.exp(-t * 2.6) * (t < 0.08 ? 1 : 0.55);
      d[i] = (Math.random() * 2 - 1) * env;
    }
  }
  const conv = ctx.createConvolver();
  conv.buffer = impulse;
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = 2200;
  const input = ctx.createGain();
  input.gain.value = 1;
  input.connect(conv);
  conv.connect(tone);
  tone.connect(destination);
  return { input };
}

export interface SampleTarget {
  /** Dry output. */
  destination: AudioNode;
  /** The reverb's input, or null for no send. */
  reverb: AudioNode | null;
}

/**
 * Play one decoded recording: detuned a little, panned, lowpassed and sent
 * to the reverb by distance. `distance` is null for a thing that happened
 * to the listener. Returns the node count, like `playVoice`.
 */
export function playSample(
  ctx: AudioContext,
  target: SampleTarget,
  buffer: AudioBuffer,
  set: SampleSet,
  gain: number,
  pan: number,
  distance: number | null,
  at = ctx.currentTime,
): number {
  let nodes = 0;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const spread = set.detune ?? 0;
  src.playbackRate.value = 1 + (Math.random() * 2 - 1) * spread;
  nodes++;

  const out = ctx.createGain();
  out.gain.value = Math.max(0, gain * (set.gain ?? 1));
  nodes++;
  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  nodes++;

  let last: AudioNode = src;
  if (distance !== null && distance > 150) {
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = distanceCutoff(distance);
    lp.Q.value = 0.5;
    last.connect(lp);
    last = lp;
    nodes++;
  }
  last.connect(out);
  out.connect(panner);
  panner.connect(target.destination);
  if (target.reverb && distance !== null) {
    const send = ctx.createGain();
    send.gain.value = reverbSend(distance);
    panner.connect(send);
    send.connect(target.reverb);
    nodes++;
  }
  src.start(at);
  src.onended = () => {
    out.disconnect();
    panner.disconnect();
  };
  return nodes;
}

/**
 * Builds and schedules one synthesised voice. Returns how many nodes it
 * cost, which is the only way to verify in a headless test that firing
 * built a graph at all.
 */
export function playVoice(ctx: AudioContext, destination: AudioNode, spec: VoiceSpec, gain: number, pan: number, at = ctx.currentTime): number {
  const now = at;
  const end = now + spec.duration;
  let nodes = 0;

  const panner = ctx.createStereoPanner();
  panner.pan.value = Math.max(-1, Math.min(1, pan));
  panner.connect(destination);
  nodes++;

  const out = ctx.createGain();
  out.gain.value = Math.max(0, gain);
  out.connect(panner);
  nodes++;

  const envelope = (peak: number, at: number, decay: number): GainNode => {
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENCE, now);
    if (at > now) g.gain.setValueAtTime(SILENCE, at);
    g.gain.linearRampToValueAtTime(Math.max(peak, SILENCE), at + Math.max(spec.attack, 0.0005));
    g.gain.exponentialRampToValueAtTime(SILENCE, at + Math.max(decay, 0.01));
    return g;
  };

  // Body: a pitched sweep.
  if (spec.bodyGain > 0) {
    const osc = ctx.createOscillator();
    osc.type = spec.bodyWave;
    osc.frequency.setValueAtTime(spec.bodyFrom, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(spec.bodyTo, 1), now + spec.bodyDecay);
    const env = envelope(spec.bodyGain, now, spec.bodyDecay);
    osc.connect(env);
    env.connect(out);
    osc.start(now);
    osc.stop(end);
    nodes += 2;
  }

  // Noise transient.
  if (spec.noiseGain > 0) {
    const src = noiseSource(ctx, false);
    src.playbackRate.value = 0.85 + Math.random() * 0.3;
    const filter = ctx.createBiquadFilter();
    filter.type = spec.noiseQ > 0 ? 'bandpass' : 'lowpass';
    filter.frequency.value = spec.noiseHz;
    if (spec.noiseQ > 0) filter.Q.value = spec.noiseQ;
    const env = envelope(spec.noiseGain, now, spec.noiseDecay);
    src.connect(filter);
    filter.connect(env);
    env.connect(out);
    src.start(now, Math.random() * 1.5);
    src.stop(end);
    nodes += 3;
  }

  // Tail: a second, later noise stage — water falling back, smoke rolling.
  if (spec.tailGain > 0) {
    const at = now + spec.tailDelay;
    const src = noiseSource(ctx, false);
    src.playbackRate.value = 0.9 + Math.random() * 0.2;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(spec.tailHz, at);
    filter.frequency.exponentialRampToValueAtTime(Math.max(spec.tailHz * 0.3, 60), at + spec.tailDecay);
    const g = ctx.createGain();
    g.gain.setValueAtTime(SILENCE, now);
    g.gain.setValueAtTime(SILENCE, at);
    g.gain.linearRampToValueAtTime(spec.tailGain, at + Math.min(0.12, spec.tailDecay * 0.3));
    g.gain.exponentialRampToValueAtTime(SILENCE, at + spec.tailDecay);
    src.connect(filter);
    filter.connect(g);
    g.connect(out);
    src.start(at, Math.random() * 1.5);
    src.stop(end);
    nodes += 3;
  }

  // Ring: inharmonic partials that sustain past the transient.
  if (spec.ringGain > 0 && spec.ringHz > 0) {
    const ratios = [1, 1.47, 2.31];
    for (let i = 0; i < ratios.length; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = spec.ringHz * ratios[i]! * (1 + (Math.random() - 0.5) * 0.01);
      const env = envelope(spec.ringGain / (i + 1), now, spec.ringDecay / (1 + i * 0.4));
      osc.connect(env);
      env.connect(out);
      osc.start(now);
      osc.stop(end);
      nodes += 2;
    }
  }

  // Tear the graph down once the last source has stopped. A stopped node
  // with no references is collected; the disconnect is for the panner and
  // gain, which never stop on their own.
  const cleanup = ctx.createConstantSource();
  cleanup.connect(ctx.createGain()); // Keeps it alive without reaching the output.
  cleanup.onended = () => {
    out.disconnect();
    panner.disconnect();
  };
  cleanup.start(now);
  cleanup.stop(end + 0.05);
  nodes++;

  return nodes;
}

/**
 * The sea and the wind, continuous. Two filtered noise beds: a low rumble
 * that swells with a slow LFO (the sea), and a higher hiss whose level
 * follows `wind`. Both parametric, so the harbour is a calm sea and light
 * air, a battle is a livelier sea, and the glasses going up muffles the
 * whole thing as the eye narrows.
 */
export class SeaBed {
  private readonly out: GainNode;
  private readonly seaGain: GainNode;
  private readonly windGain: GainNode;
  private readonly windFilter: BiquadFilterNode;
  private readonly sources: AudioScheduledSourceNode[] = [];
  /** The recorded water, once it has arrived; the noise sea fades under it. */
  private loopGain: GainNode | null = null;
  private level = { sea: 0, wind: 0, muffle: 0 };

  constructor(
    private readonly ctx: AudioContext,
    destination: AudioNode,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(destination);

    // Sea: brown-ish noise, low-passed, swelling.
    const sea = noiseSource(ctx, true);
    sea.playbackRate.value = 0.5;
    const seaFilter = ctx.createBiquadFilter();
    seaFilter.type = 'lowpass';
    seaFilter.frequency.value = 320;
    this.seaGain = ctx.createGain();
    this.seaGain.gain.value = 0;
    sea.connect(seaFilter);
    seaFilter.connect(this.seaGain);
    this.seaGain.connect(this.out);

    const swell = ctx.createOscillator();
    swell.type = 'sine';
    swell.frequency.value = 0.09;
    const swellDepth = ctx.createGain();
    swellDepth.gain.value = 0.35;
    swell.connect(swellDepth);
    swellDepth.connect(this.seaGain.gain);

    // Wind: band-passed hiss, level by wind.
    const wind = noiseSource(ctx, true);
    wind.playbackRate.value = 1;
    this.windFilter = ctx.createBiquadFilter();
    this.windFilter.type = 'bandpass';
    this.windFilter.frequency.value = 900;
    this.windFilter.Q.value = 0.5;
    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0;
    wind.connect(this.windFilter);
    this.windFilter.connect(this.windGain);
    this.windGain.connect(this.out);

    const gust = ctx.createOscillator();
    gust.type = 'sine';
    gust.frequency.value = 0.21;
    const gustDepth = ctx.createGain();
    gustDepth.gain.value = 260;
    gust.connect(gustDepth);
    gustDepth.connect(this.windFilter.frequency);

    const t = ctx.currentTime;
    for (const s of [sea, swell, wind, gust]) {
      s.start(t);
      this.sources.push(s);
    }
  }

  /**
   * Hand the bed its recording. The loop takes over the body of the sea and
   * the noise drops to the swell only; wind stays synthesised, since it is
   * a level that has to follow the weather.
   */
  useLoop(buffer: AudioBuffer): void {
    if (this.loopGain) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.loopStart = 0.05;
    src.loopEnd = Math.max(0.1, buffer.duration - 0.05);
    const lp = this.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2600;
    this.loopGain = this.ctx.createGain();
    this.loopGain.gain.value = 0;
    src.connect(lp);
    lp.connect(this.loopGain);
    this.loopGain.connect(this.out);
    src.start(this.ctx.currentTime);
    this.sources.push(src);
    this.set(this.level.sea, this.level.wind, this.level.muffle);
  }

  /** `sea` and `wind` 0…1; `muffle` 0…1 pulls the whole bed down (glasses up). */
  set(sea: number, wind: number, muffle: number): void {
    this.level = { sea, wind, muffle };
    const t = this.ctx.currentTime;
    const recorded = this.loopGain !== null;
    // The bed is background and nothing more: a fifth of what it first was,
    // well under the music, the guns and the engine. It should be felt when
    // it stops, not heard while it plays.
    this.seaGain.gain.setTargetAtTime((recorded ? 0.035 : 0.1) * sea, t, BED_GLIDE);
    if (this.loopGain) this.loopGain.gain.setTargetAtTime(0.11 * sea, t, BED_GLIDE);
    this.windGain.gain.setTargetAtTime(0.06 * wind, t, BED_GLIDE);
    this.out.gain.setTargetAtTime(1 - 0.6 * muffle, t, BED_GLIDE);
  }

  dispose(): void {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
    }
    this.out.disconnect();
  }
}

/**
 * The own ship's machinery: a low hum whose pitch and level follow the
 * throttle, and a wash of water along the hull that follows speed. Under
 * the sea bed, never over it — a captain hears the engines through the deck.
 */
export class EngineVoice {
  private readonly out: GainNode;
  private readonly hum: OscillatorNode;
  private readonly humGain: GainNode;
  private readonly washGain: GainNode;
  private readonly sources: AudioScheduledSourceNode[] = [];
  /** The recorded machinery, once it has arrived: pitch and level follow the telegraph. */
  private loop: { src: AudioBufferSourceNode; gain: GainNode } | null = null;
  private level = { throttle: 0, speed: 0, present: false };

  constructor(
    private readonly ctx: AudioContext,
    destination: AudioNode,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = 0;
    this.out.connect(destination);

    this.hum = ctx.createOscillator();
    this.hum.type = 'triangle';
    this.hum.frequency.value = 38;
    const humFilter = ctx.createBiquadFilter();
    humFilter.type = 'lowpass';
    humFilter.frequency.value = 160;
    this.humGain = ctx.createGain();
    this.humGain.gain.value = 0;
    this.hum.connect(humFilter);
    humFilter.connect(this.humGain);
    this.humGain.connect(this.out);

    const wash = noiseSource(ctx, true);
    wash.playbackRate.value = 0.7;
    const washFilter = ctx.createBiquadFilter();
    washFilter.type = 'bandpass';
    washFilter.frequency.value = 500;
    washFilter.Q.value = 0.4;
    this.washGain = ctx.createGain();
    this.washGain.gain.value = 0;
    wash.connect(washFilter);
    washFilter.connect(this.washGain);
    this.washGain.connect(this.out);

    const t = ctx.currentTime;
    this.hum.start(t);
    wash.start(t);
    this.sources.push(this.hum, wash);
  }

  useLoop(buffer: AudioBuffer): void {
    if (this.loop) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.loopStart = 0.05;
    src.loopEnd = Math.max(0.1, buffer.duration - 0.05);
    const gain = this.ctx.createGain();
    gain.gain.value = 0;
    src.connect(gain);
    gain.connect(this.out);
    src.start(this.ctx.currentTime);
    this.sources.push(src);
    this.loop = { src, gain };
    this.set(this.level.throttle, this.level.speed, this.level.present);
  }

  /** `throttle` −1…1 as the telegraph has it; `speed` 0…1 of the ship's top speed. */
  set(throttle: number, speed: number, present: boolean): void {
    this.level = { throttle, speed, present };
    const t = this.ctx.currentTime;
    const th = Math.abs(throttle);
    this.hum.frequency.setTargetAtTime(34 + th * 30, t, ENGINE_GLIDE);
    // With the recording under it the hum is the sub-bass only.
    this.humGain.gain.setTargetAtTime((this.loop ? 0.03 : 0.06) + th * (this.loop ? 0.06 : 0.16), t, ENGINE_GLIDE);
    this.washGain.gain.setTargetAtTime(speed * speed * (this.loop ? 0.1 : 0.22), t, ENGINE_GLIDE);
    if (this.loop) {
      // Idle machinery at a fifth of the level, working machinery at full, and a
      // little faster: the recording is a ship under way.
      this.loop.gain.gain.setTargetAtTime(0.12 + th * 0.5, t, ENGINE_GLIDE);
      this.loop.src.playbackRate.setTargetAtTime(0.85 + th * 0.3, t, ENGINE_GLIDE);
    }
    this.out.gain.setTargetAtTime(present ? 1 : 0, t, BED_GLIDE);
  }

  dispose(): void {
    for (const s of this.sources) {
      try {
        s.stop();
      } catch {
        // Already stopped.
      }
    }
    this.out.disconnect();
  }
}
